import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { searchPhotos } from "../lib/api";

/** Build up to 5 related-search terms from the current query. */
function buildRelated(q) {
  const base = (q || "").trim().toLowerCase();
  if (!base) return [];
  const suffixes = ["background", "landscape", "portrait", "abstract", "aesthetic"];
  const out = suffixes.map((s) => `${base} ${s}`);
  return Array.from(new Set(out)).slice(0, 5);
}

export default function RelatedSearches({ query, onPick }) {
  const [items, setItems] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    const terms = buildRelated(query);
    if (!terms.length) { setItems([]); return; }
    let cancelled = false;
    Promise.all(
      terms.map((t) =>
        searchPhotos({ query: t, per_page: 1, page: 1 })
          .then((d) => ({ term: t, photo: d.photos?.[0] || null }))
          .catch(() => ({ term: t, photo: null }))
      )
    ).then((rows) => {
      if (!cancelled) setItems(rows.filter((r) => r.photo));
    });
    return () => { cancelled = true; };
  }, [query]);

  const scrollBy = (delta) => {
    scrollRef.current?.scrollBy({ left: delta, behavior: "smooth" });
  };

  if (!items.length) return null;

  return (
    <div
      data-testid="related-searches"
      className="relative max-w-[1600px] mx-auto px-6 md:px-10 mt-6"
    >
      <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-3">
        Related searches
      </div>

      <div className="relative">
        <button
          onClick={() => scrollBy(-320)}
          className="hidden md:flex absolute -left-2 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-white border border-border shadow-sm items-center justify-center hover:border-ink"
          aria-label="Scroll left"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto es-scroll-hide pb-1 scroll-smooth"
        >
          {items.map((r) => (
            <button
              key={r.term}
              onClick={() => onPick?.(r.term)}
              data-testid={`related-chip-${r.term.replace(/\s+/g, "-")}`}
              className="group flex items-center gap-2 pl-1 pr-4 py-1 bg-white border border-border hover:border-ink transition-colors shrink-0"
            >
              <span className="h-9 w-9 shrink-0 overflow-hidden bg-secondary" style={{ backgroundColor: r.photo.avg_color || "#e9e5dd" }}>
                <img src={r.photo.src?.tiny || r.photo.src?.small} alt="" className="h-full w-full object-cover" />
              </span>
              <span className="text-sm text-ink capitalize group-hover:text-gold transition-colors">
                {r.term}
              </span>
            </button>
          ))}
        </div>

        <button
          onClick={() => scrollBy(320)}
          data-testid="related-scroll-right"
          className="hidden md:flex absolute -right-2 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-white border border-border shadow-sm items-center justify-center hover:border-ink"
          aria-label="Scroll right"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
